import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, LogOut, Settings, Upload, Star, Eye, EyeOff, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyProduct = {
  name: '',
  description: '',
  price: '',
  mrp: '',
  collection: 'pearl-edit',
  category: 'Earrings',
  image_url: '',
  images: [],
  is_featured: false,
  is_visible: true,
  is_out_of_stock: false
};

export default function AdminDashboardPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');

  const fetchProducts = useCallback(async () => {
    try {
      const url = filter === 'all' ? `${API}/admin/products` : `${API}/admin/products?collection=${filter}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setProducts(data);
    } catch {
      console.error('Failed to fetch products');
    }
  }, [filter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const openCreate = () => {
    setEditingProduct(null);
    setForm(emptyProduct);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      description: product.description,
      price: String(product.price),
      mrp: product.mrp ? String(product.mrp) : '',
      collection: product.collection,
      category: product.category || 'Earrings',
      image_url: product.image_url,
      images: product.images || [],
      is_featured: product.is_featured,
      is_visible: product.is_visible !== false,
      is_out_of_stock: product.is_out_of_stock === true,
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), mrp: form.mrp ? parseFloat(form.mrp) : null };
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      console.error('Save failed', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Delete this product permanently?')) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { withCredentials: true });
      fetchProducts();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const handleToggleVisibility = async (product) => {
    const newVisibility = product.is_visible === false ? true : false;
    try {
      await axios.patch(
        `${API}/admin/products/${product.id}/visibility`,
        { is_visible: newVisibility },
        { withCredentials: true }
      );
      fetchProducts();
    } catch (err) {
      console.error('Toggle visibility failed', err);
    }
  };

  const handleToggleStock = async (product) => {
    const newStock = product.is_out_of_stock === true ? false : true;

    try {
      await axios.patch(
        `${API}/admin/products/${product.id}/stock`,
        { is_out_of_stock: newStock },
        { withCredentials: true }
      );

      fetchProducts();
    } catch (err) {
      console.error('Toggle stock failed', err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const collectionLabel = (slug) => {
    const map = { 'pearl-edit': 'Pearl Edit', 'gold-edit': 'Gold Edit', 'after-hours': 'After Hours' };
    return map[slug] || slug;
  };

  return (
    <main data-testid="admin-dashboard" className="pt-20 min-h-screen bg-[#F6F3EE]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C6A978] mb-1">Admin Panel</p>
            <h1 className="font-['Cormorant_Garamond'] text-3xl font-light tracking-tighter text-[#1E1C1A]">
              Product Management
            </h1>
            <p className="text-sm font-light text-[#7A7369] mt-1">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              data-testid="admin-add-product-btn"
              onClick={openCreate}
              className="bg-[#1E1C1A] text-[#F6F3EE] px-4 py-2.5 text-[11px] tracking-[0.15em] uppercase hover:bg-[#3A3632] transition-colors flex items-center gap-2"
            >
              <Plus size={13} /> Add Product
            </button>
            <Link
              to="/admin/reviews"
              data-testid="admin-reviews-link"
              className="border border-[#DDD7CE] text-[#7A7369] px-4 py-2.5 text-[11px] tracking-wide hover:border-[#1E1C1A] hover:text-[#1E1C1A] transition-colors flex items-center gap-2"
            >
              <Star size={13} /> Reviews
            </Link>
            <Link
              to="/admin/settings"
              data-testid="admin-settings-link"
              className="border border-[#DDD7CE] text-[#7A7369] px-4 py-2.5 text-[11px] tracking-wide hover:border-[#1E1C1A] hover:text-[#1E1C1A] transition-colors flex items-center gap-2"
            >
              <Settings size={13} /> Settings
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="border border-[#DDD7CE] text-[#7A7369] px-4 py-2.5 text-[11px] tracking-wide hover:border-[#1E1C1A] hover:text-[#1E1C1A] transition-colors flex items-center gap-2"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <div className="flex gap-2 flex-wrap">
            {['all', 'pearl-edit', 'gold-edit', 'after-hours'].map(f => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 text-xs tracking-[0.1em] uppercase border transition-colors ${
                  filter === f
                    ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]'
                    : 'bg-transparent text-[#7A7369] border-[#DDD7CE] hover:border-[#1E1C1A] hover:text-[#1E1C1A]'
                }`}
              >
                {f === 'all' ? 'All' : collectionLabel(f)}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-[#E8E1D8] border border-[#DDD7CE]/50 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-[#DDD7CE]">
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Image</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Name</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Collection</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Price</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Featured</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Visible</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4">Stock</TableHead>
                <TableHead className="text-xs font-medium uppercase tracking-[0.1em] text-[#7A7369] py-4 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product, i) => (
                <TableRow
                  key={product.id}
                  data-testid={`product-row-${i}`}
                  className={`border-b border-[#DDD7CE]/40 ${product.is_visible === false ? 'opacity-50' : ''}`}
                >
                  <TableCell className="py-3">
                    <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover" />
                  </TableCell>
                  <TableCell className="py-3">
                    <span className="font-['Cormorant_Garamond'] text-base font-medium text-[#1E1C1A]">{product.name}</span>
                  </TableCell>
                  <TableCell className="py-3 text-sm font-light text-[#7A7369]">{collectionLabel(product.collection)}</TableCell>
                  <TableCell className="py-3 text-sm tracking-wide text-[#C6A978]">₹{product.price.toLocaleString()}</TableCell>
                  <TableCell className="py-3">
                    <span className={`text-xs uppercase tracking-wider ${product.is_featured ? 'text-[#C6A978]' : 'text-[#7A7369]'}`}>
                      {product.is_featured ? 'Yes' : 'No'}
                    </span>
                  </TableCell>
                  <TableCell className="py-3">
                    <button
                      data-testid={`toggle-visibility-${i}`}
                      onClick={() => handleToggleVisibility(product)}
                      title={product.is_visible === false ? 'Hidden — click to show' : 'Visible — click to hide'}
                      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                        product.is_visible === false
                          ? 'border-red-300 text-red-500 hover:bg-red-50'
                          : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      }`}
                    >
                      {product.is_visible === false
                        ? <><EyeOff size={11} /> Hidden</>
                        : <><Eye size={11} /> Shown</>
                      }
                    </button>
                  </TableCell>
                  <TableCell className="py-3">
                    <button
                      data-testid={`toggle-stock-${i}`}
                      onClick={() => handleToggleStock(product)}
                      title={
                        product.is_out_of_stock
                          ? 'Out of stock — click to mark in stock'
                          : 'In stock — click to mark out of stock'
                      }
                      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 border transition-colors ${
                        product.is_out_of_stock
                          ? 'border-orange-300 text-orange-600 hover:bg-orange-50'
                          : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                      }`}
                    >
                      {product.is_out_of_stock ? (
                        <>
                          <Package size={11} /> Out of Stock
                        </>
                      ) : (
                        <>
                          <Package size={11} /> In Stock
                        </>
                      )}
                    </button>
                  </TableCell>
                  <TableCell className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        data-testid={`edit-product-${i}`}
                        onClick={() => openEdit(product)}
                        className="p-2 text-[#7A7369] hover:text-[#1E1C1A] transition-colors"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        data-testid={`delete-product-${i}`}
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-[#7A7369] hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {products.length === 0 && (
            <div className="py-16 text-center text-sm font-light text-[#7A7369]">No products found</div>
          )}
        </div>
      </div>

      {/* Product Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#F6F3EE] border-[#DDD7CE] max-w-lg" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1E1C1A]">
              {editingProduct ? 'Edit Product' : 'New Product'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">Name</Label>
              <Input
                data-testid="product-name-input"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                required
                className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light"
              />
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">Description</Label>
              <Textarea
                data-testid="product-desc-input"
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                required
                rows={3}
                className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">Price (₹)</Label>
                <Input
                  type="number"
                  step="1"
                  data-testid="product-price-input"
                  value={form.price}
                  onChange={e => setForm({ ...form, price: e.target.value })}
                  required
                  className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light"
                />
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">MRP (₹)</Label>
                <Input
                  type="number"
                  step="1"
                  data-testid="product-mrp-input"
                  value={form.mrp}
                  onChange={e => setForm({ ...form, mrp: e.target.value })}
                  className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">Collection</Label>
                <Select value={form.collection} onValueChange={v => setForm({ ...form, collection: v })}>
                  <SelectTrigger data-testid="product-collection-select" className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F6F3EE] border-[#DDD7CE]">
                    <SelectItem value="pearl-edit">Pearl Edit</SelectItem>
                    <SelectItem value="gold-edit">Gold Edit</SelectItem>
                    <SelectItem value="after-hours">After Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select" className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#F6F3EE] border-[#DDD7CE]">
                    <SelectItem value="Earrings">Earrings</SelectItem>
                    <SelectItem value="Necklaces">Necklaces</SelectItem>
                    <SelectItem value="Rings">Rings</SelectItem>
                    <SelectItem value="Bracelets">Bracelets</SelectItem>
                    <SelectItem value="Pendants">Pendants</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium uppercase tracking-[0.1em] text-[#1E1C1A] mb-1.5 block">Product Image</Label>
              <div className="flex items-center gap-3">
                {form.image_url && (
                  <div className="w-14 h-14 overflow-hidden bg-[#E8E1D8] flex-shrink-0">
                    <img src={form.image_url.startsWith('http') ? form.image_url : `${process.env.REACT_APP_BACKEND_URL}${form.image_url}`} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <label
                  data-testid="product-image-upload-btn"
                  className="flex items-center gap-2 px-4 py-2 text-[11px] tracking-wide border border-[#DDD7CE] text-[#3A3632] hover:border-[#3A3632] transition-colors cursor-pointer"
                >
                  <Upload size={12} /> {form.image_url ? 'Change Image' : 'Upload Image'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append('file', file);
                      try {
                        const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                        setForm(prev => ({ ...prev, image_url: data.url }));
                      } catch (err) { console.error('Upload failed', err); }
                    }}
                  />
                </label>
              </div>
              <Input
                data-testid="product-image-input"
                value={form.image_url}
                onChange={e => setForm({ ...form, image_url: e.target.value })}
                className="bg-[#E8E1D8] border-[#DDD7CE] text-[#1E1C1A] font-light mt-2 text-xs"
                placeholder="Or paste URL..."
              />
              {/* Gallery images */}
              <div className="mt-3">
                <p className="text-[10px] font-medium uppercase tracking-widest text-[#7A7369] mb-1.5">Gallery Images (optional, shows on product page)</p>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(form.images || []).map((img, idx) => (
                    <div key={idx} className="relative w-12 h-12 bg-[#E8E1D8] overflow-hidden group flex-shrink-0">
                      <img src={img.startsWith('http') ? img : `${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full h-full object-cover" />
                      <button type="button"
                        onClick={() => setForm(prev => ({ ...prev, images: (prev.images || []).filter((_, i) => i !== idx) }))}
                        className="absolute inset-0 bg-black/60 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">✕</button>
                    </div>
                  ))}
                </div>
                <button type="button"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-widest uppercase border border-[#DDD7CE] text-[#3A3632] hover:border-[#3A3632] transition-colors"
                  onClick={async () => {
                    const input = document.createElement('input');
                    input.type = 'file'; input.accept = 'image/*'; input.multiple = true;
                    input.onchange = async (e) => {
                      const files = Array.from(e.target.files || []);
                      for (const file of files) {
                        const fd = new FormData(); fd.append('file', file);
                        try {
                          const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                          setForm(prev => ({ ...prev, images: [...(prev.images || []), data.url] }));
                        } catch(err) { console.error(err); }
                      }
                    };
                    input.click();
                  }}>
                  <Upload size={10} /> Add Gallery Photos
                </button>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="product-featured-switch"
                  checked={form.is_featured}
                  onCheckedChange={v => setForm({ ...form, is_featured: v })}
                />
                <Label className="text-sm font-light text-[#1E1C1A]">Featured</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="product-visible-switch"
                  checked={form.is_visible}
                  onCheckedChange={v => setForm({ ...form, is_visible: v })}
                />
                <Label className="text-sm font-light text-[#1E1C1A]">Visible on site</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  data-testid="product-stock-switch"
                  checked={form.is_out_of_stock}
                  onCheckedChange={v => setForm({ ...form, is_out_of_stock: v })}
                />
                <Label className="text-sm font-light text-[#1E1C1A]">
                  Out of Stock
                </Label>
              </div>
            </div>
            <button
              type="submit"
              data-testid="product-save-btn"
              disabled={saving}
              className="w-full bg-[#1E1C1A] text-[#F6F3EE] py-3 text-xs tracking-[0.2em] uppercase hover:bg-[#1E1C1A] transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingProduct ? 'Update Product' : 'Create Product'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}